import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '../lib/cn';

import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
}

const base =
  'inline-flex items-center justify-center gap-2 rounded-xl font-semibold tracking-tight ' +
  'transition-all duration-200 ease-out ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold ' +
  'active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100 select-none';

const variants: Record<ButtonVariant, string> = {
  // Gold fill: text is ink (gold fails WCAG AA against white text).
  primary:
    'bg-brand-primary text-brand-primary-fg hover:bg-brand-primary-hover hover:shadow-glow-gold',
  // Red fill: white text is fine on this red for large/bold UI text.
  secondary: 'bg-brand-secondary text-white hover:bg-brand-secondary-hover hover:shadow-glow-red',
  outline:
    'border border-border-strong/35 bg-surface text-ink hover:border-gold-400 hover:bg-gold-50/60',
  ghost: 'text-ink hover:bg-surface-subtle',
  danger: 'bg-state-danger text-white hover:bg-red-700 hover:shadow-glow-red',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-9 px-3.5 text-[13px]',
  md: 'h-11 px-5 text-sm',
  lg: 'h-12 px-6 text-[15px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    fullWidth = false,
    disabled,
    className,
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className)}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <Spinner size={size === 'lg' ? 'md' : 'sm'} />}
      {children}
    </button>
  );
});
